import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useDroppable } from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SortableItemChip } from './ItemChip'
import {
  COLOURS,
  ROW,
  SECTION_TYPE_OPTIONS,
  SOURCE,
  chipSortId,
  sectionDropId,
  sectionSortId,
} from './chipTypes'
import { kindForChip } from './useLoadListBuilder'

const Stub = () => (
  <Box
    sx={{
      width: 20,
      flex: '0 0 20px',
      alignSelf: 'flex-start',
      mt: 3,
      borderTop: `2px solid ${COLOURS.trunk}`,
    }}
  />
)

const EditableName = ({ value, placeholder, readOnly, onCommit }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])

  if (editing && !readOnly) {
    return (
      <TextField
        value={draft}
        autoFocus
        size="small"
        variant="standard"
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false)
          onCommit(draft)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur()
          if (e.key === 'Escape') {
            setDraft(value)
            setEditing(false)
          }
        }}
        inputProps={{ style: { fontWeight: 700, fontSize: '0.95rem' } }}
        sx={{ minWidth: 160 }}
      />
    )
  }
  return (
    <Typography
      variant="subtitle1"
      fontWeight={700}
      noWrap
      onClick={() => !readOnly && setEditing(true)}
      title={readOnly ? undefined : 'Click to rename'}
      sx={{
        cursor: readOnly ? 'default' : 'text',
        color: value ? 'text.primary' : 'text.disabled',
        fontStyle: value ? 'normal' : 'italic',
        borderBottom: '1px dashed transparent',
        '&:hover': readOnly ? {} : { borderBottomColor: COLOURS.trunk },
      }}
    >
      {value || placeholder}
    </Typography>
  )
}

const SectionNode = ({
  section,
  index,
  readOnly,
  isActive,
  selectedChipId,
  onSelectChip,
  onActivate,
  onRename,
  onSetType,
  onRemove,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const itemCount =
    section.requiredConsumables.length + section.requiredAssets.length

  const {
    attributes,
    listeners,
    setNodeRef: setSortRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sectionSortId(section.clientId),
    disabled: readOnly,
    data: {
      source: SOURCE.SECTION_HANDLE,
      sectionId: section.clientId,
      section,
    },
  })

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: sectionDropId(section.clientId),
    disabled: readOnly,
    data: { sectionId: section.clientId },
  })

  const handleRemove = () => {
    setMenuAnchor(null)
    if (itemCount > 0) setConfirmOpen(true)
    else onRemove(section.clientId)
  }

  return (
    <Stack
      ref={setSortRef}
      direction="row"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <Stub />
      <Paper
        ref={setDropRef}
        elevation={0}
        onClick={() => onActivate?.(section.clientId)}
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          border: '1px solid',
          borderColor: isOver
            ? COLOURS.teal
            : isActive
              ? COLOURS.assetBorder
              : COLOURS.divider,
          borderRadius: '5px',
          bgcolor: isOver ? 'rgba(35,154,164,0.06)' : 'background.paper',
          transition: 'border-color 120ms ease, background-color 120ms ease',
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
          {!readOnly && (
            <Box
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'flex',
                color: 'text.disabled',
                cursor: 'grab',
                touchAction: 'none',
                '&:active': { cursor: 'grabbing' },
              }}
              aria-label="Reorder section"
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <EditableName
              value={section.sectionName}
              placeholder={`Section ${index + 1}`}
              readOnly={readOnly}
              onCommit={(v) => onRename(section.clientId, v)}
            />
          </Box>
          <Autocomplete
            freeSolo
            size="small"
            disabled={readOnly}
            options={SECTION_TYPE_OPTIONS}
            value={section.sectionType || ''}
            onChange={(e, v) => onSetType(section.clientId, v || '')}
            onInputChange={(e, v, reason) => {
              if (reason === 'input') onSetType(section.clientId, v)
            }}
            onClick={(e) => e.stopPropagation()}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type"
                variant="outlined"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '0.72rem',
                    height: 26,
                    py: 0,
                    borderRadius: '999px',
                    bgcolor: '#F8FAFC',
                  },
                  '& input': { py: '0 !important' },
                }}
              />
            )}
            sx={{ width: 130 }}
          />
          <Typography variant="caption" color="text.secondary" noWrap>
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </Typography>
          {!readOnly && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setMenuAnchor(e.currentTarget)
              }}
              aria-label="Section options"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {section.requiredConsumables.length > 0 && (
          <SortableContext
            items={section.requiredConsumables.map((c) =>
              chipSortId(section.clientId, c.clientId),
            )}
            strategy={rectSortingStrategy}
          >
            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
              {section.requiredConsumables.map((chip) => (
                <SortableItemChip
                  key={chip.clientId}
                  sectionId={section.clientId}
                  kind={kindForChip(ROW.CONSUMABLES, chip)}
                  chip={chip}
                  selected={selectedChipId === chip.clientId}
                  readOnly={readOnly}
                  onSelect={onSelectChip}
                />
              ))}
            </Stack>
          </SortableContext>
        )}

        {section.requiredAssets.length > 0 && (
          <SortableContext
            items={section.requiredAssets.map((a) =>
              chipSortId(section.clientId, a.clientId),
            )}
            strategy={rectSortingStrategy}
          >
            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
              {section.requiredAssets.map((chip) => (
                <SortableItemChip
                  key={chip.clientId}
                  sectionId={section.clientId}
                  kind={kindForChip(ROW.ASSETS, chip)}
                  chip={chip}
                  selected={selectedChipId === chip.clientId}
                  readOnly={readOnly}
                  onSelect={onSelectChip}
                />
              ))}
            </Stack>
          </SortableContext>
        )}

        {!readOnly && (
          <Box
            sx={{
              mt: 1,
              py: 0.75,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: isOver ? COLOURS.teal : COLOURS.trunk,
              borderRadius: '5px',
              color: isOver ? COLOURS.teal : 'text.disabled',
              fontSize: '0.75rem',
            }}
          >
            {isOver ? 'Release to add here' : 'Drop items here'}
          </Box>
        )}
        {readOnly && itemCount === 0 && (
          <Typography variant="caption" color="text.disabled">
            Empty section
          </Typography>
        )}
      </Paper>

      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleRemove} sx={{ color: 'error.main' }}>
          Remove section
        </MenuItem>
      </Menu>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remove this section?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{section.sectionName || `Section ${index + 1}`}" still holds{' '}
            {itemCount} item{itemCount === 1 ? '' : 's'}. They will be removed
            from the load list too (nothing is saved until you press Save load
            list).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false)
              onRemove(section.clientId)
            }}
          >
            Remove section
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default SectionNode
